import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Shield, Heart, Eye, Zap, ArrowRight, X } from "lucide-react";
import { Button } from "../components/ui/button";

// Photo Lightbox Component
const PhotoLightbox = ({ photo, name, isOpen, onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="relative max-w-2xl max-h-[80vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute -top-12 right-0 text-white/80 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={photo}
              alt={name}
              className="w-full h-full object-contain rounded-2xl shadow-2xl"
            />
            <p className="text-center text-white/80 mt-4 text-lg font-medium">{name}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Split hero background videos
// LEFT: Person contemplating horizon, quietly hopeful, on the edge of something new
const LEFT_VIDEO = "https://assets.mixkit.co/videos/4397/4397-720.mp4"; // Silhouette of woman at sunrise, contemplative, hopeful
// RIGHT: Code actively running on dark terminal, technology feel
const RIGHT_VIDEO = "https://assets.mixkit.co/videos/9757/9757-720.mp4"; // Computer code displayed on screen

// Smooth looping video component
const SmoothVideo = ({ src, className, style }) => {
  const videoRef = useRef(null);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const timeRemaining = video.duration - video.currentTime;
      if (timeRemaining <= 0.5 && timeRemaining > 0 && !isFading) {
        setIsFading(true);
      }
    };

    const handleSeeked = () => {
      if (video.currentTime < 1) {
        setTimeout(() => setIsFading(false), 100);
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('seeked', handleSeeked);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('seeked', handleSeeked);
    };
  }, [isFading]);

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      loop
      playsInline
      className={`${className} transition-opacity duration-500 ${isFading ? 'opacity-70' : 'opacity-100'}`}
      style={style}
    >
      <source src={src} type="video/mp4" />
    </video>
  );
};

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const founders = [
  {
    name: "John Intile",
    credentials: "M.S. Cybersecurity, M.A. National Security, CISSP, PMP",
    role: "CEO & Co-Founder",
    background: "Military leader, Cybersecurity Engineer",
    bio: "John spent years building secure systems that coordinate complex operations. His background in military leadership and cybersecurity engineering taught him how to bring multiple moving parts together toward a single mission. Now he's applying those same principles to help people through life's biggest transitions.",
    expertise: ["Zero Trust Privacy Architecture", "Systems Engineering", "Military Operations Leadership"],
    photo: "https://customer-assets.emergentagent.com/job_transform-life-16/artifacts/kpfmrmc9_image.png"
  },
  {
    name: "Adrian Mirabile",
    credentials: "MSW, M.Ed., LCSW, BCBA",
    role: "Clinical Advisor & Co-Founder",
    background: "LCSW, BCBA, 20+ years clinical experience",
    bio: "Adrian spent years watching capable people fall short of their potential, not because they lacked ability, but because they lacked coordinated support. As a Licensed Clinical Social Worker and Board Certified Behavior Analyst, she's seen firsthand what helps people change. She brings two decades of wisdom to Become.",
    expertise: ["Clinical Social Work", "Behavioral Analysis", "Family & Individual Therapy"],
    photo: "https://customer-assets.emergentagent.com/job_18b3d308-20b6-4823-86a5-a4f77343f9a1/artifacts/mbfx7zk8_IMG_6587%20%281%29.JPG"
  }
];

const values = [
  {
    icon: Heart,
    name: "Human Agency",
    description: "You're in control. We provide tools and support, but your transformation journey is yours to lead."
  },
  {
    icon: Shield,
    name: "Privacy by Design",
    description: "Your data is yours. Built by a cybersecurity engineer to keep your information completely private."
  },
  {
    icon: Eye,
    name: "Clinical Rigor",
    description: "Every feature informed by clinical best practices and licensed oversight."
  },
  {
    icon: Zap,
    name: "Genuine Transformation",
    description: "We measure success by your outcomes, not your engagement metrics."
  }
];

export default function AboutPage() {
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxName, setLightboxName] = useState('');

  const openLightbox = (photo, name) => {
    setLightboxPhoto(photo);
    setLightboxName(name);
  };

  const closeLightbox = () => {
    setLightboxPhoto(null);
    setLightboxName('');
  };

  return (
    <div className="overflow-hidden">
      {/* Photo Lightbox */}
      <PhotoLightbox 
        photo={lightboxPhoto} 
        name={lightboxName} 
        isOpen={!!lightboxPhoto} 
        onClose={closeLightbox} 
      />

      {/* Hero Section, Split Screen */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden" data-testid="about-hero">
        {/* Split Background Container */}
        <div className="absolute inset-0 flex">
          {/* Left Side, Person Contemplating (Warm Tones) */}
          <div className="relative w-1/2 h-full overflow-hidden">
            <SmoothVideo
              src={LEFT_VIDEO}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: 'brightness(0.75)' }}
            />
            {/* Warm tone overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 to-transparent" />
          </div>
          
          {/* Right Side, Code/Technology (Cool Tones) */}
          <div className="relative w-1/2 h-full overflow-hidden">
            <SmoothVideo
              src={RIGHT_VIDEO}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: 'brightness(0.75)' }}
            />
            {/* Cool tone overlay */}
            <div className="absolute inset-0 bg-gradient-to-l from-blue-900/20 to-transparent" />
          </div>
        </div>
        
        {/* Center Divider Line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/20" />
        
        {/* Lighter overlay for text readability */}
        <div className="absolute inset-0 bg-deep-violet/30" />
        
        {/* Floating accent orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-20 w-80 h-80 bg-brand-peach/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-72 h-72 bg-brand-lavender/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-brand-peach font-medium tracking-widest uppercase text-sm mb-6"
          >
            Our Story
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-normal text-text-light leading-tight mb-6"
            data-testid="about-title"
          >
            About <span className="gradient-text">Become</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-text-light/70 text-lg sm:text-xl max-w-2xl mx-auto"
          >
            Born from two distinct beliefs: Every person deserves the opportunity to achieve their potential and technology exists to improve the human condition.
          </motion.p>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-24 bg-warm-bone" data-testid="origin-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-text-dark mb-6">
              The Origin Story
            </h2>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <div className="p-8 md:p-12 rounded-3xl bg-white shadow-lg border border-gray-100">
              <p className="text-text-dark/80 text-lg leading-relaxed mb-6">
                Adrian spent years watching capable people fall short of their potential, not because they lacked ability, 
                but because they lacked coordinated support. She saw single mothers trying to navigate 
                career changes while managing childcare, anxiety, and financial stress, all alone. 
                She saw veterans transitioning to civilian life without the resources to translate 
                their skills. She saw good people stuck, not because they couldn't change, but because 
                the support they needed was fragmented, expensive, and inaccessible.
              </p>

              <p className="text-text-dark/80 text-lg leading-relaxed mb-6">
                John spent years building secure systems that coordinate complex operations. In military 
                and cybersecurity environments, he learned that successful outcomes require multiple 
                specialized components working in perfect harmony, not a collection of disconnected tools.
              </p>

              <p className="text-text-dark/80 text-lg leading-relaxed mb-0">
                Together, they asked: <span className="font-semibold text-blue-600">What if we could take the coordinated, comprehensive support that only the wealthy can 
                afford and make it accessible to everyone?</span> That question became Become.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="py-24 hero-dark" data-testid="founders-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-text-light mb-4">
              Meet the <span className="gradient-text">Founders</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {founders.map((founder, index) => (
              <motion.div
                key={founder.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2, duration: 0.5 }}
                className="p-8 rounded-3xl glass-dark"
                data-testid={`founder-card-${index}`}
              >
                {/* Founder photo */}
                <div className="flex items-start gap-6 mb-6">
                  {founder.photo ? (
                    <button
                      onClick={() => openLightbox(founder.photo, founder.name)}
                      className="focus:outline-none focus:ring-2 focus:ring-brand-peach/50 rounded-2xl transition-transform hover:scale-105"
                      aria-label={`View ${founder.name}'s photo`}
                    >
                      <img 
                        src={founder.photo} 
                        alt={founder.name}
                        className="w-24 h-24 rounded-2xl object-cover object-center flex-shrink-0 cursor-pointer"
                      />
                    </button>
                  ) : (
                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-brand-peach/30 to-brand-lavender/30 flex items-center justify-center flex-shrink-0">
                      <span className="font-heading text-3xl text-text-light/50">
                        {founder.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="font-heading text-2xl text-text-light mb-1">
                      {founder.name}, <span className="text-lg text-text-light/70 font-normal">{founder.credentials}</span>
                    </h3>
                    <p className="text-brand-peach font-medium mb-2">{founder.role}</p>
                  </div>
                </div>

                <p className="text-text-light/70 leading-relaxed mb-6">{founder.bio}</p>

                <div className="mb-6">
                  <p className="text-text-light/40 text-sm uppercase tracking-wide mb-3">Expertise</p>
                  <div className="flex flex-wrap gap-2">
                    {founder.expertise.map((skill) => (
                      <span
                        key={skill}
                        className="px-3 py-1 rounded-full bg-white/5 text-text-light/70 text-sm"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-24 bg-warm-bone" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl text-text-dark mb-4">
              Our Values
            </h2>
            <p className="text-text-dark/60 text-lg max-w-2xl mx-auto">
              The principles that guide everything we build.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white border border-gray-100 shadow-soft text-center"
                data-testid={`value-card-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-peach to-brand-lavender flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-7 h-7 text-deep-violet" />
                </div>
                <h3 className="font-heading text-lg text-text-dark mb-2">{value.name}</h3>
                <p className="text-text-dark/60 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Transition to Blog */}
      <section className="py-20 bg-gradient-to-b from-warm-bone to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <p className="text-brand-primary font-medium tracking-widest uppercase text-sm mb-4">
              Insights & Updates
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-dark mb-6">
              Explore Our Blog
            </h2>
            <p className="text-text-dark/60 text-lg mb-8 max-w-2xl mx-auto">
              Dive into articles about career transitions, emotional readiness, and the latest updates from our team.
            </p>
            <Link to="/blog">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet hover:opacity-90"
                data-testid="blog-transition-btn"
              >
                Visit the Blog
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Final CTA, Signup */}
      <section className="py-32 hero-dark" data-testid="about-final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-light mb-6">
              Join Our Mission
            </h2>
            <p className="text-text-light/70 text-lg mb-10 max-w-2xl mx-auto">
              We're building something meaningful: transformation support that's accessible to everyone.
            </p>
            <Link to="/contact">
              <Button
                className="h-12 px-8 rounded-full bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet font-semibold hover:shadow-[0_0_30px_rgba(245,168,130,0.5)] transition-all hover:-translate-y-0.5"
                data-testid="signup-about-btn"
              >
                Join the Waitlist
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
